import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { postSendResetPasswordCode, postVerifyResetPasswordCode, ApiRequestError } from '@/api';
import { InlineFieldError } from '@/components/ui/FormFeedback';
import { SubpageHeader } from '@/components/ui/SubpageHeader';

const STORAGE_EMAIL_KEY = 'gf_pwd_reset_email';

/** Must match the code length from `send_reset_password_code` (currently 4 digits). */
const CODE_LEN = 6;

function resolvePasswordFlow(pathname: string) {
  const isLoginFlow = pathname.startsWith('/login/password');
  return {
    isLoginFlow,
    entryPath: isLoginFlow ? '/login/password' : '/me/password',
    loginPath: '/login',
  };
}

export default function PasswordVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { isLoginFlow, entryPath, loginPath } = resolvePasswordFlow(location.pathname);
  const loginFrom = (location.state as { from?: string } | null)?.from;
  const [email, setEmail] = useState('');
  const [digits, setDigits] = useState<string[]>(() => Array(CODE_LEN).fill(''));
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_EMAIL_KEY);
    if (!stored?.trim()) {
      navigate(entryPath, { replace: true, state: loginFrom ? { from: loginFrom } : undefined });
      return;
    }
    setEmail(stored.trim());
  }, [entryPath, loginFrom, navigate]);

  const code = digits.join('');

  const setDigitAt = useCallback((index: number, char: string) => {
    const d = char.replace(/\D/g, '').slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = d;
      return next;
    });
    if (errors.code) setErrors((e) => { const n = { ...e }; delete n.code; return n; });
  }, [errors.code]);

  const focusAt = (i: number) => {
    inputRefs.current[i]?.focus();
    inputRefs.current[i]?.select();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      e.preventDefault();
      focusAt(index - 1);
      setDigitAt(index - 1, '');
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusAt(index - 1);
    }
    if (e.key === 'ArrowRight' && index < CODE_LEN - 1) {
      e.preventDefault();
      focusAt(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const raw = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LEN);
    if (!raw) return;
    const next = Array(CODE_LEN).fill('');
    for (let i = 0; i < raw.length; i++) next[i] = raw[i];
    setDigits(next);
    if (errors.code) setErrors((er) => { const n = { ...er }; delete n.code; return n; });
    const last = Math.min(raw.length, CODE_LEN) - 1;
    if (last >= 0) setTimeout(() => focusAt(last), 0);
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (code.length !== CODE_LEN) next.code = `Enter the ${CODE_LEN}-digit code`;
    if (newPwd.length < 6) next.newPwd = 'New password must be at least 6 characters';
    if (!confirmPwd) {
      next.confirmPwd = 'Please confirm your new password';
    } else if (!newPwd) {
      next.confirmPwd = 'Enter your new password first';
    } else if (newPwd !== confirmPwd) {
      next.confirmPwd = 'Passwords do not match';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await postVerifyResetPasswordCode({
        email,
        verification_code: code,
        new_password: newPwd,
      });
      sessionStorage.removeItem(STORAGE_EMAIL_KEY);
      if (!isLoginFlow) logout();
      navigate(loginPath, {
        replace: true,
        state: {
          passwordResetOk: true,
          ...(loginFrom ? { from: loginFrom } : {}),
        },
      });
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.message : 'Something went wrong. Please try again.';
      setErrors((prev) => ({ ...prev, form: msg }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!email || resending) return;
    setResending(true);
    setErrors((prev) => { const n = { ...prev }; delete n.form; return n; });
    try {
      await postSendResetPasswordCode(email);
      setErrors((prev) => {
        const n = { ...prev };
        delete n.form;
        return n;
      });
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.message : 'Could not resend the code.';
      setErrors((prev) => ({ ...prev, form: msg }));
    } finally {
      setResending(false);
    }
  };

  if (!email) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-green-600 text-2xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      <SubpageHeader
        title="Email verification"
        subtitle="Enter the code and set a new password"
        onBack={() => navigate(entryPath, { state: loginFrom ? { from: loginFrom } : undefined })}
      />

      <div className="flex-1 px-4 py-5 space-y-6">
        <div className="bg-green-50 border border-green-100 rounded-lg px-4 py-3">
          <p className="text-green-800 text-xs leading-relaxed">
            We sent a code to <span className="font-semibold">{email}</span>. Check your inbox and enter the {CODE_LEN} digits
            below, then choose a new password.
          </p>
        </div>

        <div>
          <div className="block text-xs font-normal text-gray-500 uppercase tracking-widest mb-3">
            Verification code
          </div>
          <div className="flex gap-2 justify-center" onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={d}
                onChange={(e) => {
                  const v = e.target.value;
                  setDigitAt(i, v);
                  if (v.replace(/\D/g, '')) focusAt(Math.min(i + 1, CODE_LEN - 1));
                }}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onFocus={(e) => e.target.select()}
                aria-label={`Verification code digit ${i + 1} of ${CODE_LEN}`}
                className={`w-9 h-8 text-center text-xs font-semibold rounded-md border outline-none transition-colors text-gray-900 ${
                  errors.code ? 'border-red-300 bg-red-50/40' : 'border-gray-200 bg-white focus:border-green-500'
                }`}
              />
            ))}
          </div>
          <InlineFieldError id="pwd-verify-code-error" message={errors.code} />
        </div>

        <div>
          <label htmlFor="pwd-verify-new" className="block text-xs font-normal text-gray-500 uppercase tracking-widest mb-1.5">
            New password
          </label>
          <div className="relative">
            <input
              id="pwd-verify-new"
              type={showNew ? 'text' : 'password'}
              value={newPwd}
              onChange={(e) => {
                const v = e.target.value;
                setNewPwd(v);
                setErrors((p) => {
                  const n = { ...p };
                  if (n.newPwd) delete n.newPwd;
                  if (confirmPwd) {
                    if (v !== confirmPwd) n.confirmPwd = 'Passwords do not match';
                    else delete n.confirmPwd;
                  }
                  return n;
                });
              }}
              autoComplete="new-password"
              className={`w-full h-8 px-2.5 pr-9 rounded-md border text-xs font-normal outline-none transition-colors text-gray-800 placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                errors.newPwd ? 'border-red-300 bg-red-50/35' : 'border-gray-200 bg-white focus:border-green-500'
              }`}
              placeholder="At least 6 characters"
            />
            <button
              type="button"
              onClick={() => setShowNew((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center cursor-pointer"
              aria-label={showNew ? 'Hide password' : 'Show password'}
            >
              <i className={`${showNew ? 'ri-eye-line' : 'ri-eye-off-line'} text-gray-400 text-base`} />
            </button>
          </div>
          <InlineFieldError id="pwd-verify-new-error" message={errors.newPwd} />
        </div>

        <div>
          <label htmlFor="pwd-verify-confirm" className="block text-xs font-normal text-gray-500 uppercase tracking-widest mb-1.5">
            Confirm new password
          </label>
          <div className="relative">
            <input
              id="pwd-verify-confirm"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPwd}
              onChange={(e) => {
                const v = e.target.value;
                setConfirmPwd(v);
                setErrors((p) => {
                  const n = { ...p };
                  if (!v) {
                    n.confirmPwd = 'Please confirm your new password';
                  } else if (!newPwd) {
                    n.confirmPwd = 'Enter your new password first';
                  } else if (v !== newPwd) {
                    n.confirmPwd = 'Passwords do not match';
                  } else {
                    delete n.confirmPwd;
                  }
                  return n;
                });
              }}
              autoComplete="new-password"
              className={`w-full h-8 px-2.5 pr-9 rounded-md border text-xs font-normal outline-none transition-colors text-gray-800 placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                errors.confirmPwd ? 'border-red-300 bg-red-50/35' : 'border-gray-200 bg-white focus:border-green-500'
              }`}
              placeholder="Re-enter new password"
            />
            <button
              type="button"
              onClick={() => setShowConfirm((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center cursor-pointer"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              <i className={`${showConfirm ? 'ri-eye-line' : 'ri-eye-off-line'} text-gray-400 text-base`} />
            </button>
          </div>
          <InlineFieldError id="pwd-verify-confirm-error" message={errors.confirmPwd} />
        </div>

        {errors.form && (
          <div className="text-red-600 text-sm bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            {errors.form}
          </div>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full h-9 bg-green-500 hover:bg-green-600 text-white font-medium text-xs rounded-lg cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin text-sm" />
              Submitting…
            </>
          ) : (
            <>
              <i className="ri-shield-check-line text-sm" />
              Verify and update password
            </>
          )}
        </button>

        <button
          type="button"
          onClick={handleResend}
          disabled={resending}
          className="w-full text-green-600 font-medium text-xs py-2 cursor-pointer disabled:opacity-50"
        >
          {resending ? 'Sending…' : "Didn't get the email? Resend code"}
        </button>
      </div>
    </div>
  );
}
